/**
 * Namespaced front-end logger. Every catch / unexpected branch in the UI
 * should funnel through this so production failures leave a trace.
 *
 *   import { logger } from "@/lib/log";
 *   const log = logger("tasks");
 *   log.warn("active-task load failed", err);
 *
 * Wiring a remote reporter (Sentry / PostHog / Datadog) is a single call:
 *
 *   setErrorReporter((entry) => Sentry.captureMessage(entry.message, ...));
 *
 * The reporter only receives `warn` and `error` entries.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogEntry = {
  level: LogLevel;
  tag: string;
  message: string;
  data?: unknown;
};

export type ErrorReporter = (entry: LogEntry) => void;

let _reporter: ErrorReporter | null = null;

export function setErrorReporter(reporter: ErrorReporter | null): void {
  _reporter = reporter;
}

function emit(level: LogLevel, tag: string, message: string, data?: unknown): void {
  const prefix = `[${tag}]`;
  if (typeof console !== "undefined") {
    const fn =
      level === "error" ? console.error
      : level === "warn" ? console.warn
      : level === "info" ? console.info
      : console.debug;
    if (data !== undefined) fn(prefix, message, data);
    else fn(prefix, message);
  }
  if (_reporter && (level === "warn" || level === "error")) {
    try {
      _reporter({ level, tag, message, data });
    } catch {
      // A broken reporter must never crash the app.
    }
  }
}

export type Logger = {
  debug: (message: string, data?: unknown) => void;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

export function logger(tag: string): Logger {
  return {
    debug: (message, data) => emit("debug", tag, message, data),
    info: (message, data) => emit("info", tag, message, data),
    warn: (message, data) => emit("warn", tag, message, data),
    error: (message, data) => emit("error", tag, message, data),
  };
}
